/**
 * Filter Section Header Component
 *
 * Displays a collapsible section header with title and toggle-all checkbox.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

type FilterSectionHeaderProps = {
  title: string;
  itemCount: number;
  selectedCount: number;
  onToggleAll: () => void;
};

export function FilterSectionHeader({
  title,
  itemCount,
  selectedCount,
  onToggleAll,
}: FilterSectionHeaderProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const allSelected = selectedCount === itemCount;
  const someSelected = selectedCount > 0 && selectedCount < itemCount;

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.checkboxArea}
        onPress={onToggleAll}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <View
          style={[
            styles.checkbox,
            {
              borderColor: allSelected || someSelected ? palette.primary : palette.border,
              backgroundColor: allSelected ? palette.primary : 'transparent',
            },
          ]}
        >
          {allSelected && (
            <Ionicons name="checkmark" size={12} color="#FFFFFF" />
          )}
          {someSelected && (
            <View style={[styles.partialIndicator, { backgroundColor: palette.primary }]} />
          )}
        </View>
      </TouchableOpacity>

      <Text style={[styles.title, { color: palette.text }]}>
        {title}
      </Text>

      <Text style={[styles.count, { color: palette.textMuted }]}>
        {selectedCount}/{itemCount}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm + 2,
    paddingTop: Spacing.md + 2,
  },
  checkboxArea: {
    padding: 4,
    marginRight: Spacing.sm,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  partialIndicator: {
    width: 8,
    height: 2,
    borderRadius: 1,
  },
  title: {
    flex: 1,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    opacity: 0.8,
    fontFamily: getBodyFont(),
  },
  count: {
    fontSize: 11,
    fontFamily: getBodyFont(),
  },
});
